import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { HttpService } from './http.service';

/**
 * Client for the unified document pipeline (Phase 1 of the printing
 * standardisation). The document is rendered ONCE server-side and the same
 * PDF is used for preview, download, print and email — the frontend never
 * renders a document itself.
 *
 * Valid documentType values are the PipelineDocumentTypes constants
 * (constants/pipeline-document-types.ts).
 */
/** A batch render request: several entities rendered as one paged PDF. */
export interface DocumentBatchRequest {
  documentType: string;
  reportIds: string[];
  /** The target printer's media, so the PDF matches what the printer expects. */
  documentSize?: string;
  pageWidthInches?: number;
  pageHeightInches?: number;
  copies?: number;
  title?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentPipelineService {
  private readonly httpService = inject(HttpService);

  private readonly apiUrl = 'TmsWeb/DocumentPipeline';

  /** Server-rendered PDF for inline display (blob). */
  preview(documentType: string, reportId: string, title?: string): Observable<Blob> {
    return this.httpService.postForBlob(`${this.apiUrl}/Preview`, this.toRequest(documentType, reportId, title));
  }

  /** The same server-rendered PDF, for saving as a file (blob). */
  download(documentType: string, reportId: string, title?: string): Observable<Blob> {
    return this.httpService.postForBlob(`${this.apiUrl}/Download`, this.toRequest(documentType, reportId, title));
  }

  /**
   * Several entities as ONE PDF — a batch of labels, one per page — sized for
   * the printer that will receive it. Always prefer this over calling
   * download() per id: the server renders the whole batch in a single pass.
   */
  downloadBatch(options: DocumentBatchRequest): Observable<Blob> {
    return this.httpService.postForBlob(`${this.apiUrl}/Download`, options);
  }

  /**
   * The same batch, for INLINE display — the bulk-print preview (several despatch
   * notes as one paged PDF). Same request shape and the same single render as
   * downloadBatch; only the endpoint differs, because Preview must never trigger
   * the printed-status side effects that Download does.
   */
  previewBatch(options: DocumentBatchRequest): Observable<Blob> {
    return this.httpService.postForBlob(`${this.apiUrl}/Preview`, options);
  }

  /** The same batch, rendered and submitted straight to PrintNode. */
  printBatch(options: DocumentBatchRequest & { printerId: number }): Observable<{ printJobId: number }> {
    return this.httpService.post<{ printJobId: number }>(`${this.apiUrl}/Print`, options);
  }

  /** Renders server-side and submits the PDF to PrintNode. */
  print(
    documentType: string,
    reportId: string,
    printerId: number,
    copies = 1,
    title?: string,
  ): Observable<{ printJobId: number }> {
    return this.httpService.post<{ printJobId: number }>(`${this.apiUrl}/Print`, {
      ...this.toRequest(documentType, reportId, title),
      printerId,
      copies,
    });
  }

  /**
   * Most documents are addressed by a Guid (reportId); a few use a string key
   * (e.g. the Purchase Delivery Note's delivery note number). The API's
   * ReportId field is a Guid, so non-Guid ids travel as reportKey instead.
   *
   * A BATCH sends reportIds instead — see downloadBatch / previewBatch.
   */
  private toRequest(documentType: string, reportId: string, title?: string) {
    const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(reportId ?? '');
    return isGuid ? { documentType, reportId, title } : { documentType, reportKey: reportId, title };
  }

  /** Triggers a browser save of a PDF blob. */
  saveBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Opens the browser print dialog for a PDF blob via a hidden iframe, so the
   * user prints the real rendered document (not the on-screen HTML). The
   * iframe is cleaned up after a delay — there is no reliable cross-browser
   * event for the print dialog closing.
   */
  printBlob(blob: Blob): void {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        URL.revokeObjectURL(url);
        iframe.remove();
      }, 60000);
    };
    document.body.appendChild(iframe);
  }

  /** Wraps a PDF blob in the FormData shape the email/upload endpoints expect. */
  toFormData(blob: Blob, fileName: string): FormData {
    const formData = new FormData();
    formData.append('pdf', blob, fileName);
    return formData;
  }

  /**
   * Extracts the API's error message from a failed pipeline call so the user
   * is told WHY it failed, not just that it failed. The API returns
   * BadRequest with a plain message; blob requests (preview/download) deliver
   * that error body as a Blob, JSON requests (print) as an already-parsed value.
   * Falls back to the supplied message when no usable body is present.
   */
  async getErrorMessage(error: unknown, fallback: string): Promise<string> {
    let body = (error as HttpErrorResponse)?.error;
    if (body instanceof Blob) {
      try {
        body = await body.text();
      } catch {
        return fallback;
      }
    }
    if (typeof body === 'string' && body.length > 0) {
      try {
        // BadRequest(string) arrives JSON-encoded (quoted); unwrap when possible
        const parsed = JSON.parse(body);
        return typeof parsed === 'string' && parsed.length > 0 ? parsed : fallback;
      } catch {
        return body;
      }
    }
    return fallback;
  }
}
